import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Flex,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Image,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Select,
  Textarea,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { SearchIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  FaFlask,
  FaSignOutAlt,
  FaVial,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaDollarSign,
  FaClipboardList,
  FaUpload,
  FaDownload,
  FaUsers,
  FaInbox,
  FaHourglassHalf,
  FaChartBar,
  FaCopy,
  FaKey,
  FaUserPlus,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaFileInvoiceDollar,
  FaPlus,
  FaStream,
  FaCalendarPlus,
} from 'react-icons/fa';
import LabTestMenuUpload from './LabTestMenuUpload';
import jsPDF from 'jspdf';

import { getSubmissionStatusColor as getStatusColor, getSubmissionStatusLabel as getStatusLabel, getLabTimelineStatus, getLabTimelineConfig, getLabTimelineOrder, getOutcomeConfig } from '../../utils/statusBadge';
import { logSubmissionOutcome } from '../../utils/logOutcome';
import LabWeeklyInvoicesTab from './LabWeeklyInvoicesTab';
import { tagColor as submissionTagColor } from '../../utils/submissionTags';

// ── Shared helpers ──────────────────────────────────────────────────────────────

// Who logged a status event → how to label it on the lab side.
const ACTOR_BADGE = {
  admin: { label: 'Admin', color: 'gray' },
  lab:   { label: 'Lab',   color: 'purple' },
  phleb: { label: 'Phleb', color: 'blue' },
};
const actorBadge = (kind) => ACTOR_BADGE[kind] || ACTOR_BADGE.phleb;

// ISO timestamp → value for a <input type="datetime-local"> in local time.
const toLocalDateTimeInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── Export helpers ────────────────────────────────────────────────────────────

// Quote a CSV cell only when needed; objects/arrays are JSON-stringified so no
// data is lost in the flat export.
const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  let s = typeof val === 'object' ? JSON.stringify(val) : String(val);
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
};

// Flatten rows to CSV using the union of all keys across the rows, so every
// field present on any row gets a column.
const rowsToCSV = (rows) => {
  if (!rows.length) return '';
  const keys = Array.from(rows.reduce((set, r) => {
    Object.keys(r || {}).forEach((k) => set.add(k));
    return set;
  }, new Set()));
  const header = keys.map(csvEscape).join(',');
  const body = rows.map((r) => keys.map((k) => csvEscape(r ? r[k] : '')).join(','));
  return [header, ...body].join('\r\n');
};

const downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};
// Normalize DB field into an array of valid URLs
const normalizeToUrls = (value) => {
  try {
    const supabaseBase = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
    const toFullUrl = (u) => {
      if (!u || typeof u !== 'string') return null;
      const trimmed = u.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      if (supabaseBase && /storage\/v1\/object\/public\//.test(trimmed)) {
        return trimmed.startsWith('http') ? trimmed : `${supabaseBase}/${trimmed.replace(/^\//, '')}`;
      }
      return null;
    };

    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          const raw = typeof item === 'string' ? item : (item && typeof item === 'object' && typeof item.url === 'string' ? item.url : null);
          return toFullUrl(raw);
        })
        .filter((u) => typeof u === 'string');
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr)) {
          return arr
            .map((item) => {
              const raw = typeof item === 'string' ? item : (item && typeof item === 'object' ? item.url : null);
              return toFullUrl(raw);
            })
            .filter((u) => typeof u === 'string');
        }
        return [];
      }
      if (trimmed.includes(',')) return trimmed.split(',').map(s => toFullUrl(s)).filter(Boolean);
      const single = toFullUrl(trimmed);
      return single ? [single] : [];
    }
    return [];
  } catch {
    return [];
  }
};

// ── Stat Card ───────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color, helpText, isActive, onClick }) => (
  <Box
    bg="white"
    p={5}
    borderRadius="xl"
    boxShadow="sm"
    border="2px solid"
    borderColor={isActive ? `${color}.400` : 'transparent'}
    cursor={onClick ? 'pointer' : 'default'}
    onClick={onClick}
    transition="all 0.2s"
    _hover={onClick ? { borderColor: `${color}.300`, transform: 'translateY(-1px)', boxShadow: 'md' } : {}}
  >
    <HStack spacing={4}>
      <Flex
        w={12}
        h={12}
        borderRadius="lg"
        bg={`${color}.50`}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
      </Flex>
      <Box>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1">
          {value}
        </Text>
        <Text fontSize="sm" color="gray.500" mt={0.5}>
          {label}
        </Text>
      </Box>
    </HStack>
  </Box>
);

// ── File Preview ────────────────────────────────────────────────────────────────

const FilePreview = ({ url, label }) => {
  if (!url) return null;
  const safeUrl = typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
  if (!safeUrl) return null;

  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
        {label}
      </Text>
      {isImage ? (
        <Image src={safeUrl} alt={label} maxH="180px" maxW="280px" objectFit="contain" borderRadius="md" border="1px solid" borderColor="gray.200" />
      ) : (
        <Button as="a" href={safeUrl} target="_blank" rel="noopener noreferrer" size="sm" colorScheme="blue" variant="outline" leftIcon={<FaEye />}>
          View {label}
        </Button>
      )}
    </Box>
  );
};

// ── Submissions Table ───────────────────────────────────────────────────────────

const SubmissionsTable = ({ submissions, batchById = {}, onViewSubmission, onMarkReceived, onUploadResults, onShowTimeline, onSchedule, emptyIcon, emptyTitle, emptyMessage }) => {
  if (submissions.length === 0) {
    return (
      <Box py={16} textAlign="center">
        <Icon as={emptyIcon || FaInbox} boxSize={16} color="gray.200" mb={4} />
        <Text fontSize="lg" fontWeight="semibold" color="gray.500" mb={1}>
          {emptyTitle || 'No submissions'}
        </Text>
        <Text color="gray.400" fontSize="sm" maxW="400px" mx="auto">
          {emptyMessage || 'Submissions will appear here when they are available.'}
        </Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto" w="100%">
      <Table variant="simple" size="sm" sx={{ 'th, td': { py: 2, px: 3 } }}>
        <Thead bg="gray.50">
          <Tr>
            <Th fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" w="320px">Actions</Th>
            <Th fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" w="170px">Stage</Th>
            <Th fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Patient</Th>
            <Th fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Doctor</Th>
            <Th fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" w="110px">Date</Th>
          </Tr>
        </Thead>
        <Tbody>
          {submissions.map((submission) => {
            const batch = submission.batch_id ? batchById[submission.batch_id] : null;
            const timeline = getLabTimelineStatus(submission, batch);
            const received = !!submission.received_at_lab || timeline.code === 'waiting_results' || timeline.code === 'results_posted';
            return (
            <Tr
              key={submission.id}
              _hover={{ bg: 'gray.50' }}
              transition="background 0.15s"
            >
              <Td>
                {/* Actions moved to the leading column so Timeline + Details
                    are the first thing the eye lands on. */}
                <HStack spacing={1.5} flexWrap="wrap" rowGap={1}>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    leftIcon={<FaEye />}
                    onClick={() => onViewSubmission(submission)}
                  >
                    Details
                  </Button>
                  <Button
                    size="xs"
                    colorScheme="gray"
                    variant="outline"
                    leftIcon={<FaStream />}
                    onClick={() => onShowTimeline && onShowTimeline(submission)}
                  >
                    Timeline
                  </Button>
                  {onSchedule && !received && !['completed', 'cancelled', 'out_of_area'].includes((submission.status || '').toLowerCase()) && (
                    <Button
                      size="xs"
                      colorScheme="teal"
                      variant="outline"
                      leftIcon={<FaCalendarPlus />}
                      onClick={() => onSchedule(submission)}
                    >
                      Schedule
                    </Button>
                  )}
                  {received ? (
                    <Badge fontSize="2xs" colorScheme="green" borderRadius="full" px={2}>Received</Badge>
                  ) : (
                    <Button
                      size="xs"
                      colorScheme="purple"
                      variant="solid"
                      onClick={() => onMarkReceived(submission)}
                    >
                      Mark Received
                    </Button>
                  )}
                  {(submission.status === 'waiting_on_lab_results' && !submission.lab_results_url) && (
                    <Button
                      size="xs"
                      colorScheme="orange"
                      leftIcon={<FaUpload />}
                      onClick={() => onUploadResults(submission)}
                    >
                      Upload
                    </Button>
                  )}
                </HStack>
              </Td>
              <Td>
                <Tooltip label={timeline.description} placement="top">
                  <Badge
                    colorScheme={timeline.color}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    fontSize="2xs"
                    fontWeight="semibold"
                  >
                    {timeline.label}
                  </Badge>
                </Tooltip>
              </Td>
              <Td>
                <Text fontWeight="600" fontSize="sm" color="gray.800" noOfLines={1}>{submission.patient_name}</Text>
                {submission.patient_email && (
                  <Text fontSize="2xs" color="gray.400" noOfLines={1}>{submission.patient_email}</Text>
                )}
                {Array.isArray(submission.tags) && submission.tags.length > 0 && (
                  <HStack spacing={1} mt={1} flexWrap="wrap">
                    {submission.tags.map((t) => (
                      <Badge key={t} colorScheme={submissionTagColor(t)} borderRadius="full" px={2} fontSize="2xs" textTransform="none">
                        {t}
                      </Badge>
                    ))}
                  </HStack>
                )}
              </Td>
              <Td>
                <Text fontSize="sm" color="gray.700" noOfLines={1}>{submission.doctor_name}</Text>
              </Td>
              <Td>
                <Text color="gray.600" fontSize="xs">{formatDate(submission.submitted_at)}</Text>
              </Td>
            </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────────

const LabPortalDashboard = () => {
  const [labSession, setLabSession] = useState(null);
  const [labAgreementData, setLabAgreementData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [batches, setBatches] = useState({}); // { batch_id: batchRow } — used by the lab timeline derivation
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [timelineSubmission, setTimelineSubmission] = useState(null); // submission whose timeline drawer is open
  const [timelineEvents, setTimelineEvents] = useState([]); // all status notes for the open timeline
  const [timelineEventsLoading, setTimelineEventsLoading] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState('all'); // 'all' or a timeline code
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilters, setTagFilters] = useState([]); // array of tag strings — match-any
  const [doctorFilters, setDoctorFilters] = useState([]); // doctor names — match-any
  const [clientCodeFilters, setClientCodeFilters] = useState([]); // practice client codes — match-any
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Status history (submission_status_events) for the open submission.
  const [statusEvents, setStatusEvents] = useState([]);
  const [statusEventsLoading, setStatusEventsLoading] = useState(false);
  // Lab-side scheduling.
  const [scheduleSubmission, setScheduleSubmission] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const { isOpen: isScheduleOpen, onOpen: onScheduleOpen, onClose: onScheduleClose } = useDisclosure();
  const [secretKeys, setSecretKeys] = useState([]);
  const [secretKeysLoading, setSecretKeysLoading] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newSecretKey, setNewSecretKey] = useState('');
  const [keyToDelete, setKeyToDelete] = useState(null);
  const { isOpen: isDeleteConfirmOpen, onOpen: onDeleteConfirmOpen, onClose: onDeleteConfirmClose } = useDisclosure();
  const toast = useToast();
  const uploadSectionRef = React.useRef(null);
  const navigate = useNavigate();

  // ── Session & Data Loading ──────────────────────────────────────────────────

  useEffect(() => {
    const loadDashboard = async () => {
      const sessionData = localStorage.getItem('labSession');
      if (!sessionData) {
        navigate('/lab-portal/login');
        return;
      }

      try {
        const parsedSession = JSON.parse(sessionData);

        let labData = null;
        let userName = parsedSession.user_name || 'Admin';

        // Validate session: check 24-hour expiry
        const loginTime = new Date(parsedSession.loginTime);
        const now = new Date();
        const hoursElapsed = (now - loginTime) / (1000 * 60 * 60);
        if (hoursElapsed > 24) {
          localStorage.removeItem('labSession');
          toast({
            title: 'Session Expired',
            description: 'Your session has expired. Please log in again.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          navigate('/lab-portal/login');
          return;
        }

        // Validate by lab ID + active status (no secret key needed)
        const { data: mainLabData, error: mainError } = await supabase
          .from('labs')
          .select('id, name, logo_url, is_active')
          .eq('id', parsedSession.id)
          .eq('is_active', true)
          .single();

        if (mainLabData && !mainError) {
          labData = mainLabData;
        }

        if (!labData) {
          localStorage.removeItem('labSession');
          toast({
            title: 'Session Invalid',
            description: 'Please log in again',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          navigate('/lab-portal/login');
          return;
        }

        const validatedSession = {
          id: labData.id,
          name: labData.name,
          logo_url: labData.logo_url,
          user_name: userName,
          is_main_key: parsedSession.is_main_key ?? true,
          loginTime: parsedSession.loginTime || new Date().toISOString(),
        };

        localStorage.setItem('labSession', JSON.stringify(validatedSession));
        setLabSession(validatedSession);

        await Promise.all([
          fetchSubmissions(validatedSession.id, validatedSession),
          fetchLabAgreementData(validatedSession.id, validatedSession)
        ]);
      } catch (error) {
        console.error('Session validation error:', error);
        localStorage.removeItem('labSession');
        navigate('/lab-portal/login');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();

    // Real-time sync: auto-update when admin changes submission status
    const channel = supabase
      .channel('lab-portal-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'submissions' }, () => {
        const session = JSON.parse(localStorage.getItem('labSession') || 'null');
        if (session?.id) fetchSubmissions(session.id, session);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [navigate]);

  const fetchLabAgreementData = async (labId, session = null) => {
    try {
      const activeSession = session || labSession;
      if (!activeSession || activeSession.id !== labId) return;

      const { data, error } = await supabase
        .from('labs')
        .select(`
          id, name, partnership_status, subscription_plan, monthly_fee,
          agreement_signed, agreement_date, signer_name, signer_title, signature_data,
          enrollment_date, contact_email, state, city
        `)
        .eq('id', labId)
        .single();

      if (error) throw error;
      if (data && data.id !== labId) return;

      setLabAgreementData(data);
    } catch (error) {
      console.error('Error fetching lab agreement data:', error);
    }
  };

  // If URL has #test-directory, default to that tab
  useEffect(() => {
    if (window.location.hash.includes('test-directory')) {
      setTabIndex(2);
      setTimeout(() => {
        uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, []);

  // ── Submissions ─────────────────────────────────────────────────────────────

  const fetchSubmissions = async (labId, session = null) => {
    try {
      const activeSession = session || labSession;

      if (!labId || !activeSession || activeSession.id !== labId) {
        toast({
          title: 'Session Error',
          description: 'Please log in again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        if (!labId || !activeSession) {
          localStorage.removeItem('labSession');
          navigate('/lab-portal/login');
        }
        return;
      }

      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select('*')
        .eq('lab_id', labId)
        // Include every status the new outcome flow can produce — without
        // 'needs_reschedule' / 'waiting_for_required_info' / 'cancelled' the
        // lab wouldn't see rows the phleb just moved into those buckets.
        .in('status', [
          'pending',
          'in_progress',
          'waiting_to_be_received',
          'waiting_on_lab_results',
          'completed',
          'needs_reschedule',
          'waiting_for_required_info',
          'cancelled',
        ])
        .order('submitted_at', { ascending: false }));

      if (error) throw error;

      // Security: filter to only this lab's submissions
      const labIdString = String(labId);
      const validSubmissions = (data || []).filter(s => String(s.lab_id) === labIdString);

      setSubmissions(validSubmissions);

      // Fetch the submission batches referenced by these submissions so the timeline derivation
      // can tell Batched apart from In Transit (relies on submission_batches.shipped_at — added in Phase 2)
      const batchIds = [...new Set(validSubmissions.map(s => s.batch_id).filter(Boolean))];
      if (batchIds.length > 0) {
        // Phase 2: also pull shipping fields so the timeline derivation can distinguish
        // Batched / In Transit. Fields exist only after running add_lab_timeline_fields.sql.
        const { data: batchRows } = await supabase
          .from('submission_batches')
          .select('id, batch_number, status, ship_fee, created_at, shipped_at, carrier, tracking_number, estimated_arrival')
          .in('id', batchIds);
        const map = {};
        (batchRows || []).forEach(b => { map[b.id] = b; });
        setBatches(map);
      } else {
        setBatches({});
      }
    } catch (error) {
      toast({
        title: 'Error fetching submissions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReceived = async (submission) => {
    try {
      const receivedAt = new Date().toISOString();
      const receivedBy = labSession?.id || null;
      const { error } = await supabase
        .from('submissions')
        .update({
          status: 'completed',
          received_at_lab: receivedAt,
          received_at_lab_by: receivedBy,
        })
        .eq('id', submission.id);
      if (error) throw error;

      setSubmissions(prev => prev.map(s => s.id === submission.id ? {
        ...s,
        status: 'completed',
        received_at_lab: receivedAt,
        received_at_lab_by: receivedBy,
      } : s));
      if (selectedSubmission?.id === submission.id) {
        setSelectedSubmission(prev => ({
          ...prev,
          status: 'completed',
          received_at_lab: receivedAt,
          received_at_lab_by: receivedBy,
        }));
      }
      toast({ title: 'Marked as received', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Failed to update', description: e.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleUploadResults = async (file, submission) => {
    if (!file) return;
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `lab-results/${submission.id}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('lab-results')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        toast({ title: 'Upload failed', description: uploadError.message, status: 'error', duration: 5000, isClosable: true });
        return;
      }

      const { data: urlData } = supabase.storage.from('lab-results').getPublicUrl(filePath);
      const resultsUrl = urlData.publicUrl;

      const { error: updateError } = await supabase
        .from('submissions')
        .update({ lab_results_url: resultsUrl, status: 'completed' })
        .eq('id', submission.id);

      if (updateError) {
        toast({ title: 'Error saving results', description: updateError.message, status: 'error', duration: 5000, isClosable: true });
        return;
      }

      setSubmissions(prev => prev.map(s =>
        s.id === submission.id ? { ...s, lab_results_url: resultsUrl, status: 'completed' } : s
      ));
      setSelectedSubmission(prev => prev?.id === submission.id ? { ...prev, lab_results_url: resultsUrl, status: 'completed' } : prev);

      toast({ title: 'Results uploaded', description: 'Submission marked as completed', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  // ── Secret Key Management ───────────────────────────────────────────────────

  const fetchSecretKeys = async () => {
    if (!labSession?.id) return;
    setSecretKeysLoading(true);
    try {
      const { data, error } = await supabase
        .from('lab_secret_keys')
        .select('*')
        .eq('lab_id', labSession.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSecretKeys(data || []);
    } catch (error) {
      toast({ title: 'Error fetching secret keys', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setSecretKeysLoading(false);
    }
  };

  const generateSecretKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  const handleCreateSecretKey = async () => {
    if (!newUserName.trim()) {
      toast({ title: 'User name required', description: 'Please enter a name for this user', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    const secretKey = newSecretKey.trim() || generateSecretKey();

    try {
      const { error } = await supabase
        .from('lab_secret_keys')
        .insert([{
          lab_id: labSession.id,
          secret_key: secretKey,
          user_name: newUserName.trim(),
          is_active: true,
        }]);

      if (error) throw error;

      toast({ title: 'Secret key created', description: `New key created for ${newUserName.trim()}`, status: 'success', duration: 3000, isClosable: true });
      setNewUserName('');
      setNewSecretKey('');
      await fetchSecretKeys();
    } catch (error) {
      toast({ title: 'Error creating secret key', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleToggleSecretKey = async (keyId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('lab_secret_keys')
        .update({ is_active: !currentStatus })
        .eq('id', keyId);

      if (error) throw error;
      toast({ title: currentStatus ? 'Key deactivated' : 'Key activated', status: 'success', duration: 2000, isClosable: true });
      await fetchSecretKeys();
    } catch (error) {
      toast({ title: 'Error updating secret key', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleDeleteSecretKey = (keyId) => {
    setKeyToDelete(keyId);
    onDeleteConfirmOpen();
  };

  const confirmDeleteSecretKey = async () => {
    if (!keyToDelete) return;
    try {
      const { error } = await supabase
        .from('lab_secret_keys')
        .delete()
        .eq('id', keyToDelete);

      if (error) throw error;
      toast({ title: 'Secret key deleted', status: 'success', duration: 3000, isClosable: true });
      await fetchSecretKeys();
      onDeleteConfirmClose();
      setKeyToDelete(null);
    } catch (error) {
      toast({ title: 'Error deleting secret key', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  useEffect(() => {
    // Tab 3 = Team (only when is_main_key !== false). Sub-key users have a
    // shifted tab layout where index 3 is "Weekly Invoices" instead — don't
    // fire the secret-keys fetch for them.
    if (tabIndex === 3 && labSession?.id && labSession?.is_main_key !== false) {
      fetchSecretKeys();
    }
  }, [tabIndex, labSession?.id, labSession?.is_main_key]);

  // ── Agreement PDF ───────────────────────────────────────────────────────────

  const generateAgreementPDF = () => {
    if (!labAgreementData || !labAgreementData.agreement_signed) {
      toast({ title: 'Agreement Not Signed', description: 'You must sign the agreement before downloading the PDF', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    const doc = new jsPDF();
    doc.setProperties({ title: 'Laboratory Partnership Agreement', subject: 'At Home Blood Draw Inc Partnership Agreement', author: 'At Home Blood Draw Inc', creator: 'Lab Portal System' });

    let yPos = 20;
    const pageWidth = 210;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Laboratory Partnership Agreement', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    const defaultTemplate = `LABORATORY PARTNERSHIP AGREEMENT

This Agreement is entered into between At Home Blood Draw Inc ("Company") and {LAB_NAME} ("Laboratory").

1. SERVICES
Laboratory agrees to provide clinical laboratory testing services for samples collected through Company's mobile phlebotomy network.

2. PLACEMENT PRIORITY
Laboratory will receive {PLACEMENT_PRIORITY} placement priority in Company's selection algorithm based on the {PLAN_NAME} subscription plan.

3. BILLING
Laboratory agrees to pay a monthly fee of $\{MONTHLY_FEE} for access to Company's platform and services, billed every 28 days.

4. QUALITY STANDARDS
Laboratory warrants that all testing will be performed in accordance with applicable regulatory standards including CLIA, CAP, and state regulations.

5. DATA SHARING
Laboratory agrees to provide test results in standardized formats and maintain patient confidentiality in accordance with HIPAA regulations.

6. TERM
This agreement shall remain in effect until terminated by either party with 30 days written notice.

By signing below, the authorized representative of Laboratory acknowledges reading, understanding, and agreeing to be bound by the terms of this Agreement.`;

    const storedTemplate = localStorage.getItem('labAgreementTemplate');
    const template = storedTemplate || defaultTemplate;

    const signedDate = labAgreementData.agreement_date ? formatDate(labAgreementData.agreement_date) : 'Not signed';
    const agreementText = template
      .replace(/{LAB_NAME}/g, labAgreementData.name || 'Laboratory')
      .replace(/{PLAN_NAME}/g, labAgreementData.subscription_plan || 'Standard')
      .replace(/{PLACEMENT_PRIORITY}/g, labAgreementData.subscription_plan === 'premium' ? 'Premium' : labAgreementData.subscription_plan === 'standard' ? 'Standard' : labAgreementData.subscription_plan === 'professional' ? 'Professional' : labAgreementData.subscription_plan === 'enterprise' ? 'Enterprise' : labAgreementData.subscription_plan === 'starter' ? 'Starter' : 'Basic')
      .replace(/{MONTHLY_FEE}/g, String(labAgreementData.monthly_fee || '0'))
      .replace(/{LAB_STATE}/g, labAgreementData.state || 'N/A')
      .replace(/{LAB_CITY}/g, labAgreementData.city || 'N/A')
      .replace(/{SIGNED_DATE}/g, signedDate)
      .replace(/{AGREEMENT_DATE}/g, signedDate);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(agreementText, contentWidth);

    lines.forEach((line) => {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.text(line, margin, yPos);
      yPos += 6;
    });

    doc.addPage();
    yPos = 50;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURES', pageWidth / 2, yPos, { align: 'center' });
    yPos += 20;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Laboratory:', margin, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(labAgreementData.name || 'Laboratory', margin + 40, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 10;

    if (labAgreementData.signer_name) {
      doc.text(`By: ${labAgreementData.signer_name}`, margin, yPos);
      if (labAgreementData.signer_title) {
        doc.text(`Title: ${labAgreementData.signer_title}`, margin + 80, yPos);
      }
      yPos += 10;
    }

    if (labAgreementData.signature_data) {
      try {
        doc.addImage(labAgreementData.signature_data, 'PNG', margin, yPos, 60, 20);
        yPos += 25;
      } catch (error) {
        doc.line(margin, yPos + 10, margin + 60, yPos + 10);
        yPos += 20;
      }
    } else {
      doc.line(margin, yPos + 10, margin + 60, yPos + 10);
      yPos += 20;
    }

    if (labAgreementData.agreement_date) {
      doc.text(`Date: ${formatDate(labAgreementData.agreement_date)}`, margin, yPos);
    }
    yPos += 30;

    const companySignature = localStorage.getItem('companySignature');
    const companySignerName = localStorage.getItem('companySignerName') || 'Authorized Representative';
    const companySignerTitle = localStorage.getItem('companySignerTitle') || 'Manager';

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('At Home Blood Draw Inc:', margin + 90, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text('At Home Blood Draw Inc', margin + 90, yPos + 10);
    yPos += 20;

    doc.setFont('helvetica', 'normal');
    doc.text(`By: ${companySignerName}`, margin + 90, yPos);
    yPos += 8;
    doc.text(`Name: ${companySignerName}`, margin + 90, yPos);
    yPos += 8;
    doc.text(`Title: ${companySignerTitle}`, margin + 90, yPos);
    yPos += 15;

    if (companySignature) {
      try {
        doc.addImage(companySignature, 'PNG', margin + 90, yPos, 60, 20);
        yPos += 25;
      } catch (error) {
        doc.line(margin + 90, yPos + 10, margin + 150, yPos + 10);
        yPos += 20;
      }
    } else {
      doc.line(margin + 90, yPos, margin + 150, yPos);
      yPos += 20;
    }
    yPos += 8;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin + 90, yPos);

    const filename = `Agreement-${labAgreementData.name.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);

    toast({ title: 'PDF Downloaded', status: 'success', duration: 3000, isClosable: true });
  };

  // ── Navigation & Actions ────────────────────────────────────────────────────

  const handleLogout = () => {
    localStorage.removeItem('labSession');
    navigate('/lab-portal/login');
    toast({ title: 'Logged out', status: 'success', duration: 2000, isClosable: true });
  };

  // Load the full disposition history (phleb / admin / lab) for a submission.
  const fetchStatusEvents = async (submissionId) => {
    if (!submissionId) { setStatusEvents([]); return; }
    setStatusEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from('submission_status_events')
        .select('id, outcome_code, outcome_notes, status, blood_collection_time, logged_at, logged_by_kind')
        .eq('submission_id', submissionId)
        .order('logged_at', { ascending: false });
      if (error) throw error;
      setStatusEvents(data || []);
    } catch (e) {
      console.warn('Could not load status events:', e.message);
      setStatusEvents([]);
    } finally {
      setStatusEventsLoading(false);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setStatusEvents([]);
    fetchStatusEvents(submission.id);
    onOpen();
  };

  // Load every note for the submission whose timeline is open, so the lifecycle
  // modal can show the full back-and-forth instead of just the latest note.
  useEffect(() => {
    const id = timelineSubmission?.id;
    if (!id) { setTimelineEvents([]); return; }
    let cancelled = false;
    (async () => {
      setTimelineEventsLoading(true);
      try {
        const { data, error } = await supabase
          .from('submission_status_events')
          .select('id, outcome_code, outcome_notes, status, logged_at, logged_by_kind')
          .eq('submission_id', id)
          .order('logged_at', { ascending: false });
        if (error) throw error;
        if (!cancelled) setTimelineEvents(data || []);
      } catch (e) {
        if (!cancelled) setTimelineEvents([]);
      } finally {
        if (!cancelled) setTimelineEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [timelineSubmission?.id]);

  // ── Lab-side scheduling ──────────────────────────────────────────────────
  const handleOpenSchedule = (submission) => {
    setScheduleSubmission(submission);
    setScheduleTime(toLocalDateTimeInput(submission.blood_collection_time));
    setScheduleNote('');
    onScheduleOpen();
  };

  const handleSaveSchedule = async () => {
    if (!scheduleSubmission) return;
    if (!scheduleTime) {
      toast({ title: 'Pick a date & time', status: 'warning', duration: 2500 });
      return;
    }
    setScheduleSaving(true);
    try {
      const iso = new Date(scheduleTime).toISOString();
      const { updateFields } = await logSubmissionOutcome({
        submission: scheduleSubmission,
        code: 'scheduled',
        note: scheduleNote || null,
        scheduledIso: iso,
        loggedByKind: 'lab',
        loggedById: labSession?.id || null,
      });
      setSubmissions((prev) => prev.map((s) => (s.id === scheduleSubmission.id ? { ...s, ...updateFields } : s)));
      setSelectedSubmission((prev) => (prev?.id === scheduleSubmission.id ? { ...prev, ...updateFields } : prev));
      if (selectedSubmission?.id === scheduleSubmission.id) fetchStatusEvents(scheduleSubmission.id);
      toast({ title: 'Appointment scheduled', status: 'success', duration: 2500, isClosable: true });
      onScheduleClose();
    } catch (e) {
      toast({ title: 'Could not schedule', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setScheduleSaving(false);
    }
  };

  // ── Computed Data ───────────────────────────────────────────────────────────

  // Anything still in flight for this lab. Previously this list excluded
  // needs_reschedule / waiting_for_required_info / cancelled, which silently
  // hid 30+ patients per lab (admin assigned them, but the lab never saw the
  // row). The chip filters below — Rescheduling, Awaiting Info, Cancelled —
  // let the lab isolate each bucket; the union still tells them "this is
  // every patient you should expect from us".
  const waitingSubmissions = submissions.filter(s =>
    s.status === 'pending' ||
    s.status === 'in_progress' ||
    s.status === 'waiting_to_be_received' ||
    s.status === 'needs_reschedule' ||
    s.status === 'waiting_for_required_info' ||
    s.status === 'cancelled' ||
    (s.status === 'waiting_on_lab_results' && !s.lab_results_url) ||
    // Post-draw submissions go straight to 'completed' now (the old in-transit
    // status is retired) — keep them inbound until the lab posts results.
    (s.status === 'completed' && !s.lab_results_url)
  );

  const completedSubmissions = submissions.filter(s => s.status === 'completed' && s.lab_results_url);

  // Sub-filter by timeline code (set via the chip buttons above the Inbound table)
  const filterByTimeline = (list) => {
    if (timelineFilter === 'all') return list;
    return list.filter(s => {
      const batch = s.batch_id ? batches[s.batch_id] : null;
      return getLabTimelineStatus(s, batch).code === timelineFilter;
    });
  };

  // Counts per timeline code across the inbound list — used to render counts on each chip
  const timelineCounts = waitingSubmissions.reduce((acc, s) => {
    const batch = s.batch_id ? batches[s.batch_id] : null;
    const code = getLabTimelineStatus(s, batch).code;
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  const filterBySearch = (list) => {
    let out = list;
    if (tagFilters.length > 0) {
      const wanted = tagFilters.map((t) => t.toLowerCase());
      out = out.filter((s) =>
        Array.isArray(s.tags) && s.tags.some((t) => wanted.includes(String(t).toLowerCase()))
      );
    }
    if (doctorFilters.length > 0) {
      const wanted = new Set(doctorFilters.map((d) => d.toLowerCase()));
      out = out.filter((s) => s.doctor_name && wanted.has(String(s.doctor_name).toLowerCase()));
    }
    if (clientCodeFilters.length > 0) {
      const wanted = new Set(clientCodeFilters);
      out = out.filter((s) => s.client_code && wanted.has(String(s.client_code)));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      out = out.filter((s) =>
        s.patient_name?.toLowerCase().includes(q) ||
        s.doctor_name?.toLowerCase().includes(q) ||
        s.client_code?.toLowerCase().includes(q) ||
        (s.submitted_at && formatDate(s.submitted_at).toLowerCase().includes(q)) ||
        (Array.isArray(s.tags) && s.tags.some((t) => String(t).toLowerCase().includes(q)))
      );
    }
    return out;
  };

  // Export the currently-filtered inbound rows (same set the table shows) as
  // CSV or JSON. "All data" = the full submission rows, not just the visible columns.
  const handleExport = async (format) => {
    const rows = filterByTimeline(filterBySearch(waitingSubmissions));
    if (rows.length === 0) {
      toast({ title: 'Nothing to export', description: 'No rows match the current filters.', status: 'warning', duration: 3000 });
      return;
    }

    // Pull every note for these rows so the export carries the full history,
    // not just the latest denormalized outcome_notes.
    const eventsBySubmission = {};
    try {
      const ids = rows.map((r) => r.id);
      const { data: events } = await supabase
        .from('submission_status_events')
        .select('submission_id, outcome_code, outcome_notes, status, logged_at, logged_by_kind')
        .in('submission_id', ids)
        .order('logged_at', { ascending: true });
      (events || []).forEach((ev) => {
        if (!eventsBySubmission[ev.submission_id]) eventsBySubmission[ev.submission_id] = [];
        eventsBySubmission[ev.submission_id].push(ev);
      });
    } catch (e) {
      console.warn('Could not load notes for export:', e.message);
    }

    const rowsWithNotes = rows.map((r) => {
      const evs = eventsBySubmission[r.id] || [];
      const all_notes = evs
        .filter((e) => e.outcome_notes)
        .map((e) => {
          const when = e.logged_at ? new Date(e.logged_at).toLocaleString() : '';
          const who = actorBadge(e.logged_by_kind).label;
          const label = e.outcome_code ? (getOutcomeConfig(e.outcome_code)?.label || e.outcome_code) : '';
          const meta = [when, who, label].filter(Boolean).join(' · ');
          return `[${meta}] ${e.outcome_notes}`;
        })
        .join('  ||  ');
      // origin_ref is our placer identifier — the lab should echo it back (ideally
      // as the HL7 Placer Order Number) so re-transmitted orders are recognized
      // as ours and not re-ingested as duplicates.
      return { origin_ref: r.origin_ref || `AHBD-${r.id}`, ...r, all_notes, status_events: evs };
    });

    const stamp = new Date().toISOString().slice(0, 10);
    const labSlug = (labSession?.name || 'lab').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    if (format === 'json') {
      downloadFile(JSON.stringify(rowsWithNotes, null, 2), `${labSlug}-submissions-${stamp}.json`, 'application/json');
    } else {
      // Prepend a BOM so Excel reads UTF-8 correctly.
      downloadFile('﻿' + rowsToCSV(rowsWithNotes), `${labSlug}-submissions-${stamp}.csv`, 'text/csv;charset=utf-8;');
    }
    toast({ title: `Exported ${rowsWithNotes.length} row${rowsWithNotes.length === 1 ? '' : 's'} as ${format.toUpperCase()}`, status: 'success', duration: 2500 });
  };

  // Distinct tags across every submission, for the filter dropdown.
  const availableTags = useMemo(() => {
    const s = new Set();
    submissions.forEach((row) => {
      if (Array.isArray(row.tags)) row.tags.forEach((t) => t && s.add(String(t)));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [submissions]);

  // Distinct doctor names across every submission, for the filter dropdown.
  const availableDoctors = useMemo(() => {
    const s = new Set();
    submissions.forEach((row) => { if (row.doctor_name) s.add(String(row.doctor_name).trim()); });
    return Array.from(s).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [submissions]);

  // Distinct practice client codes, with the doctors seen under each so the
  // dropdown reads "5435 — Gerald Williams" instead of a bare number.
  const availableClientCodes = useMemo(() => {
    const byCode = new Map();
    submissions.forEach((row) => {
      const code = row.client_code && String(row.client_code).trim();
      if (!code) return;
      if (!byCode.has(code)) byCode.set(code, new Set());
      if (row.doctor_name) byCode.get(code).add(String(row.doctor_name).trim());
    });
    return Array.from(byCode.entries())
      .map(([code, docs]) => {
        const names = Array.from(docs).sort((a, b) => a.localeCompare(b));
        const label = names.length ? `${code} — ${names.slice(0, 2).join(', ')}${names.length > 2 ? '…' : ''}` : code;
        return { code, label };
      })
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [submissions]);

  // ── Loading State ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.500" fontSize="sm">Loading your dashboard...</Text>
        </VStack>
      </Flex>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box minH="100vh" bg="gray.50">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={3}>
          <Flex justify="space-between" align="center">
            {/* Left: Lab identity */}
            <HStack spacing={3}>
              {labSession?.logo_url ? (
                <Image src={labSession.logo_url} alt={labSession.name} h="36px" objectFit="contain" />
              ) : (
                <Flex w={9} h={9} borderRadius="lg" bg="blue.500" align="center" justify="center">
                  <Icon as={FaFlask} color="white" boxSize={4} />
                </Flex>
              )}
              <Box>
                <Heading size="sm" color="gray.800">{labSession?.name}</Heading>
                {labSession?.user_name && labSession.user_name !== 'Admin' && (
                  <Text fontSize="xs" color="gray.500">Logged in as {labSession.user_name}</Text>
                )}
              </Box>
            </HStack>

            {/* Right: Actions */}
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FaPlus />}
                onClick={() => navigate('/lab-portal/new-draw')}
              >
                New Draw
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaDollarSign />}
                onClick={() => navigate('/lab-portal/test-costs')}
                display={{ base: 'none', md: 'flex' }}
              >
                Test Costs
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaFileInvoiceDollar />}
                onClick={() => navigate(`/lab/${labSession?.id}/invoices`)}
                display={{ base: 'none', md: 'flex' }}
              >
                Invoices
              </Button>
              {labAgreementData?.agreement_signed && (
                <Tooltip label="Download Agreement PDF">
                  <IconButton
                    icon={<FaDownload />}
                    size="sm"
                    variant="ghost"
                    aria-label="Download Agreement PDF"
                    onClick={generateAgreementPDF}
                  />
                </Tooltip>
              )}
              <Menu>
                <MenuButton as={Button} size="sm" variant="ghost" rightIcon={<ChevronDownIcon />}>
                  More
                </MenuButton>
                <MenuList>
                  <MenuItem icon={<FaDollarSign />} onClick={() => navigate('/lab-portal/test-costs')} display={{ base: 'flex', md: 'none' }}>
                    Test Costs
                  </MenuItem>
                  <MenuItem icon={<FaFileInvoiceDollar />} onClick={() => navigate(`/lab/${labSession?.id}/invoices`)} display={{ base: 'flex', md: 'none' }}>
                    Invoices
                  </MenuItem>
                  <MenuItem icon={<FaClipboardList />} onClick={() => navigate('/lab-portal/quick-start')}>
                    Quick Start Guide
                  </MenuItem>
                  {labAgreementData?.agreement_signed && (
                    <MenuItem icon={<FaDownload />} onClick={generateAgreementPDF}>
                      Download Agreement
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem icon={<FaSignOutAlt />} onClick={handleLogout} color="red.500">
                    Log Out
                  </MenuItem>
                </MenuList>
              </Menu>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<FaSignOutAlt />}
                onClick={handleLogout}
                display={{ base: 'none', md: 'flex' }}
              >
                Log Out
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <Container maxW="container.2xl" py={6}>
        <VStack spacing={6} align="stretch">

          {/* ── Stats Row ─────────────────────────────────────────────────── */}
          <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
            <StatCard
              icon={FaInbox}
              label="Inbound Samples"
              value={waitingSubmissions.length}
              color="purple"
              onClick={() => setTabIndex(0)}
              isActive={tabIndex === 0}
            />
            <StatCard
              icon={FaHourglassHalf}
              label="Awaiting Results"
              value={submissions.filter(s => s.status === 'waiting_on_lab_results' && !s.lab_results_url).length}
              color="orange"
              onClick={() => setTabIndex(0)}
              isActive={false}
            />
            <StatCard
              icon={FaCheckCircle}
              label="Completed"
              value={completedSubmissions.length}
              color="green"
              onClick={() => setTabIndex(1)}
              isActive={tabIndex === 1}
            />
            <StatCard
              icon={FaChartBar}
              label="Total"
              value={submissions.length}
              color="blue"
              onClick={() => setTabIndex(0)}
              isActive={false}
            />
          </SimpleGrid>

          {/* ── Tabs ──────────────────────────────────────────────────────── */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden" border="1px solid" borderColor="gray.100">
            <Tabs index={tabIndex} onChange={(i) => { setTabIndex(i); setSearchQuery(''); }} isLazy>
              <Box borderBottom="1px solid" borderColor="gray.100">
                <TabList border="none" px={2} pt={1}>
                  <Tab
                    py={3}
                    px={4}
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.500"
                    _selected={{ color: 'blue.600', borderColor: 'blue.500', fontWeight: 'semibold' }}
                  >
                    <Icon as={FaInbox} mr={2} boxSize={3.5} />
                    Inbound ({waitingSubmissions.length})
                  </Tab>
                  <Tab
                    py={3}
                    px={4}
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.500"
                    _selected={{ color: 'green.600', borderColor: 'green.500', fontWeight: 'semibold' }}
                  >
                    <Icon as={FaCheckCircle} mr={2} boxSize={3.5} />
                    Completed ({completedSubmissions.length})
                  </Tab>
                  <Tab
                    py={3}
                    px={4}
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.500"
                    _selected={{ color: 'purple.600', borderColor: 'purple.500', fontWeight: 'semibold' }}
                  >
                    <Icon as={FaClipboardList} mr={2} boxSize={3.5} />
                    Test Directory
                  </Tab>
                  {labSession?.is_main_key !== false && (
                    <Tab
                      py={3}
                      px={4}
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.500"
                      _selected={{ color: 'orange.600', borderColor: 'orange.500', fontWeight: 'semibold' }}
                    >
                      <Icon as={FaUsers} mr={2} boxSize={3.5} />
                      Team
                    </Tab>
                  )}
                  <Tab
                    py={3}
                    px={4}
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.500"
                    _selected={{ color: 'teal.600', borderColor: 'teal.500', fontWeight: 'semibold' }}
                  >
                    <Icon as={FaFileInvoiceDollar} mr={2} boxSize={3.5} />
                    Weekly Invoices
                  </Tab>
                </TabList>
              </Box>

              <TabPanels>
                {/* ── Inbound Tab ──────────────────────────────────────────── */}
                <TabPanel p={0}>
                  <Box px={5} pt={4} pb={2}>
                    <HStack spacing={3} align="center">
                      <InputGroup size="md" flex={1}>
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.300" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search by patient name, doctor, client code, date, or tag..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          borderRadius="lg"
                          bg="gray.50"
                          border="1px solid"
                          borderColor="gray.200"
                          _focus={{ bg: 'white', borderColor: 'blue.300' }}
                        />
                      </InputGroup>
                      {availableDoctors.length > 0 && (
                        <Select
                          size="md"
                          maxW="240px"
                          placeholder={doctorFilters.length > 0 ? `Doctors · ${doctorFilters.length}` : 'Filter by doctor…'}
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            setDoctorFilters((prev) => (prev.includes(v) ? prev : [...prev, v]));
                          }}
                          bg="gray.50"
                          borderRadius="lg"
                        >
                          {availableDoctors
                            .filter((d) => !doctorFilters.includes(d))
                            .map((d) => (<option key={d} value={d}>{d}</option>))}
                        </Select>
                      )}
                      {availableClientCodes.length > 0 && (
                        <Select
                          size="md"
                          maxW="240px"
                          placeholder={clientCodeFilters.length > 0 ? `Client codes · ${clientCodeFilters.length}` : 'Filter by client code…'}
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            setClientCodeFilters((prev) => (prev.includes(v) ? prev : [...prev, v]));
                          }}
                          bg="gray.50"
                          borderRadius="lg"
                        >
                          {availableClientCodes
                            .filter(({ code }) => !clientCodeFilters.includes(code))
                            .map(({ code, label }) => (<option key={code} value={code}>{label}</option>))}
                        </Select>
                      )}
                      {availableTags.length > 0 && (
                        <Select
                          size="md"
                          maxW="220px"
                          placeholder={tagFilters.length > 0 ? `Tags · ${tagFilters.length}` : 'Filter by tag…'}
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            setTagFilters((prev) => (prev.includes(v) ? prev : [...prev, v]));
                          }}
                          bg="gray.50"
                          borderRadius="lg"
                        >
                          {availableTags
                            .filter((t) => !tagFilters.includes(t))
                            .map((t) => (<option key={t} value={t}>{t}</option>))}
                        </Select>
                      )}
                      <Menu>
                        <MenuButton
                          as={Button}
                          size="md"
                          variant="outline"
                          colorScheme="blue"
                          borderRadius="lg"
                          leftIcon={<FaDownload />}
                          rightIcon={<ChevronDownIcon />}
                          flexShrink={0}
                        >
                          Export
                        </MenuButton>
                        <MenuList>
                          <MenuItem icon={<FaDownload />} onClick={() => handleExport('csv')}>Export CSV</MenuItem>
                          <MenuItem icon={<FaDownload />} onClick={() => handleExport('json')}>Export JSON</MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                    {(tagFilters.length > 0 || doctorFilters.length > 0 || clientCodeFilters.length > 0) && (
                      <HStack spacing={2} mt={2} flexWrap="wrap">
                        {clientCodeFilters.length > 0 && (
                          <>
                            <Text fontSize="xs" color="gray.500" fontWeight="600">Client codes:</Text>
                            {clientCodeFilters.map((c) => (
                              <Badge
                                key={c}
                                colorScheme="purple"
                                borderRadius="full"
                                px={2}
                                fontSize="xs"
                                cursor="pointer"
                                onClick={() => setClientCodeFilters((prev) => prev.filter((x) => x !== c))}
                                title="Click to remove"
                              >
                                {c} ×
                              </Badge>
                            ))}
                          </>
                        )}
                        {doctorFilters.length > 0 && (
                          <>
                            <Text fontSize="xs" color="gray.500" fontWeight="600">Doctors:</Text>
                            {doctorFilters.map((d) => (
                              <Badge
                                key={d}
                                colorScheme="teal"
                                borderRadius="full"
                                px={2}
                                fontSize="xs"
                                cursor="pointer"
                                onClick={() => setDoctorFilters((prev) => prev.filter((x) => x !== d))}
                                title="Click to remove"
                              >
                                {d} ×
                              </Badge>
                            ))}
                          </>
                        )}
                        {tagFilters.length > 0 && (
                          <>
                            <Text fontSize="xs" color="gray.500" fontWeight="600" ml={doctorFilters.length > 0 ? 3 : 0}>Tags:</Text>
                            {tagFilters.map((t) => (
                              <Badge
                                key={t}
                                colorScheme={submissionTagColor(t)}
                                borderRadius="full"
                                px={2}
                                fontSize="xs"
                                cursor="pointer"
                                onClick={() => setTagFilters((prev) => prev.filter((x) => x !== t))}
                                title="Click to remove"
                              >
                                {t} ×
                              </Badge>
                            ))}
                          </>
                        )}
                        <Button size="xs" variant="ghost" onClick={() => { setTagFilters([]); setDoctorFilters([]); setClientCodeFilters([]); }}>Clear all</Button>
                      </HStack>
                    )}
                  </Box>
                  {/* Timeline filter chips — shows lab where each inbound sample is in its lifecycle */}
                  <Box px={5} pb={3}>
                    <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                      <Badge
                        as="button"
                        type="button"
                        onClick={() => setTimelineFilter('all')}
                        colorScheme={timelineFilter === 'all' ? 'blue' : 'gray'}
                        variant={timelineFilter === 'all' ? 'solid' : 'subtle'}
                        px={3}
                        py={1.5}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="700"
                        cursor="pointer"
                      >
                        All ({waitingSubmissions.length})
                      </Badge>
                      {getLabTimelineOrder()
                        .filter(code => code !== 'results_posted')
                        .map(code => {
                          const cfg = getLabTimelineConfig(code);
                          const count = timelineCounts[code] || 0;
                          if (count === 0 && timelineFilter !== code) return null;
                          const active = timelineFilter === code;
                          return (
                            <Badge
                              key={code}
                              as="button"
                              type="button"
                              onClick={() => setTimelineFilter(code)}
                              colorScheme={cfg.color}
                              variant={active ? 'solid' : 'subtle'}
                              px={3}
                              py={1.5}
                              borderRadius="full"
                              fontSize="xs"
                              fontWeight="700"
                              cursor="pointer"
                              opacity={count === 0 ? 0.4 : 1}
                            >
                              {cfg.label} ({count})
                            </Badge>
                          );
                        })}
                    </HStack>
                  </Box>
                  <Box px={2} pb={4}>
                    <SubmissionsTable
                      submissions={filterByTimeline(filterBySearch(waitingSubmissions))}
                      batchById={batches}
                      onViewSubmission={handleViewSubmission}
                      onMarkReceived={handleMarkReceived}
                      onUploadResults={(s) => handleViewSubmission(s)}
                      onShowTimeline={(s) => setTimelineSubmission(s)}
                      onSchedule={handleOpenSchedule}
                      emptyIcon={FaInbox}
                      emptyTitle="No inbound samples"
                      emptyMessage="When blood draw submissions are on their way to your lab, they'll appear here."
                    />
                  </Box>
                </TabPanel>

                {/* ── Completed Tab ────────────────────────────────────────── */}
                <TabPanel p={0}>
                  <Box px={5} pt={4} pb={2}>
                    <InputGroup size="md">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search by patient name, doctor, or date..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        borderRadius="lg"
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.200"
                        _focus={{ bg: 'white', borderColor: 'blue.300' }}
                      />
                    </InputGroup>
                  </Box>
                  <Box px={2} pb={4}>
                    <SubmissionsTable
                      submissions={filterBySearch(completedSubmissions)}
                      batchById={batches}
                      onViewSubmission={handleViewSubmission}
                      onMarkReceived={handleMarkReceived}
                      onUploadResults={(s) => handleViewSubmission(s)}
                      onShowTimeline={(s) => setTimelineSubmission(s)}
                      onSchedule={handleOpenSchedule}
                      emptyIcon={FaCheckCircle}
                      emptyTitle="No completed submissions yet"
                      emptyMessage="Completed submissions with uploaded results will appear here."
                    />
                  </Box>
                </TabPanel>

                {/* ── Test Directory Tab ───────────────────────────────────── */}
                <TabPanel p={6}>
                  <VStack spacing={6} align="stretch" id="test-directory" ref={uploadSectionRef}>
                    <Box>
                      <Heading size="md" mb={2}>Test Directory</Heading>
                      <Text color="gray.500" fontSize="sm">
                        Upload your complete test menu to showcase your services to patients and providers.
                        Our team will review and format your directory for optimal visibility.
                      </Text>
                    </Box>
                    <LabTestMenuUpload labId={labSession?.id} labName={labSession?.name} />
                  </VStack>
                </TabPanel>

                {/* ── Team Members Tab ─────────────────────────────────────── */}
                {labSession?.is_main_key !== false && (
                  <TabPanel p={6}>
                    <VStack spacing={6} align="stretch">
                      <Box>
                        <Heading size="md" mb={1}>Team Members</Heading>
                        <Text color="gray.500" fontSize="sm">
                          Create and manage access keys for your team. Each key allows a member to log in and view submissions.
                        </Text>
                      </Box>

                      {/* Create New Key */}
                      <Box p={5} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                        <HStack mb={4}>
                          <Icon as={FaUserPlus} color="blue.500" />
                          <Text fontWeight="semibold" color="gray.700">Add Team Member</Text>
                        </HStack>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <FormControl>
                            <FormLabel fontSize="sm">Name</FormLabel>
                            <Input
                              placeholder="e.g., John - Technician"
                              value={newUserName}
                              onChange={(e) => setNewUserName(e.target.value)}
                              bg="white"
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Secret Key (optional)</FormLabel>
                            <Input
                              placeholder="Auto-generated if blank"
                              value={newSecretKey}
                              onChange={(e) => setNewSecretKey(e.target.value)}
                              fontFamily="mono"
                              bg="white"
                            />
                          </FormControl>
                        </SimpleGrid>
                        <Button
                          mt={4}
                          colorScheme="blue"
                          size="sm"
                          onClick={handleCreateSecretKey}
                          isLoading={secretKeysLoading}
                          leftIcon={<FaKey />}
                        >
                          Create Key
                        </Button>
                      </Box>

                      {/* Existing Keys */}
                      {secretKeysLoading ? (
                        <Flex justify="center" py={8}>
                          <Spinner />
                        </Flex>
                      ) : secretKeys.length === 0 ? (
                        <Box py={12} textAlign="center">
                          <Icon as={FaUsers} boxSize={12} color="gray.200" mb={3} />
                          <Text color="gray.500" fontWeight="medium">No team members yet</Text>
                          <Text color="gray.400" fontSize="sm">Create a key above to get started</Text>
                        </Box>
                      ) : (
                        <VStack spacing={3} align="stretch">
                          {secretKeys.map((key) => (
                            <Box
                              key={key.id}
                              p={4}
                              bg="white"
                              border="1px solid"
                              borderColor={key.is_active ? 'gray.200' : 'red.100'}
                              borderRadius="lg"
                              opacity={key.is_active ? 1 : 0.7}
                              transition="all 0.2s"
                              _hover={{ boxShadow: 'sm' }}
                            >
                              <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                                <Box flex={1} minW="200px">
                                  <HStack spacing={2} mb={1}>
                                    <Text fontWeight="semibold" color="gray.800">{key.user_name}</Text>
                                    {!key.is_active && <Badge colorScheme="red" fontSize="2xs">Inactive</Badge>}
                                  </HStack>
                                  <HStack spacing={2}>
                                    <Text fontSize="xs" fontFamily="mono" color="gray.500" bg="gray.50" px={2} py={0.5} borderRadius="md">
                                      {key.secret_key}
                                    </Text>
                                    <Tooltip label="Copy key">
                                      <IconButton
                                        icon={<FaCopy />}
                                        size="xs"
                                        variant="ghost"
                                        aria-label="Copy key"
                                        onClick={() => {
                                          navigator.clipboard.writeText(key.secret_key);
                                          toast({ title: 'Copied!', status: 'info', duration: 1500 });
                                        }}
                                      />
                                    </Tooltip>
                                  </HStack>
                                  <Text fontSize="2xs" color="gray.400" mt={1}>
                                    Created {formatDate(key.created_at)}
                                    {key.last_used_at && ` \u00B7 Last used ${formatDate(key.last_used_at)}`}
                                  </Text>
                                </Box>
                                <HStack spacing={2}>
                                  <Tooltip label={key.is_active ? 'Deactivate' : 'Activate'}>
                                    <IconButton
                                      icon={key.is_active ? <FaToggleOn /> : <FaToggleOff />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme={key.is_active ? 'green' : 'gray'}
                                      aria-label={key.is_active ? 'Deactivate' : 'Activate'}
                                      onClick={() => handleToggleSecretKey(key.id, key.is_active)}
                                    />
                                  </Tooltip>
                                  <Tooltip label="Delete key">
                                    <IconButton
                                      icon={<FaTrash />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      aria-label="Delete key"
                                      onClick={() => handleDeleteSecretKey(key.id)}
                                    />
                                  </Tooltip>
                                </HStack>
                              </Flex>
                            </Box>
                          ))}
                        </VStack>
                      )}

                      <Alert status="info" borderRadius="lg" bg="blue.50" border="1px solid" borderColor="blue.100">
                        <AlertIcon color="blue.400" />
                        <Box>
                          <AlertTitle fontSize="sm">How team access works</AlertTitle>
                          <AlertDescription fontSize="xs" color="gray.600">
                            Each key grants login access to your lab portal. All team members see the same submissions.
                            Only the main account holder can manage team members.
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </VStack>
                  </TabPanel>
                )}
                <TabPanel p={0}>
                  <LabWeeklyInvoicesTab labId={labSession?.id} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>

      {/* ── Timeline Drawer ──────────────────────────────────────────────────── */}
      <Modal isOpen={!!timelineSubmission} onClose={() => setTimelineSubmission(null)} size="md" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
            <HStack spacing={3}>
              <Flex w={10} h={10} borderRadius="lg" bg="purple.50" align="center" justify="center">
                <Icon as={FaClock} color="purple.500" />
              </Flex>
              <Box>
                <Text fontSize="md" fontWeight="bold">Sample Lifecycle</Text>
                <Text fontSize="xs" color="gray.500">{timelineSubmission?.patient_name}</Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            {timelineSubmission && (() => {
              const batch = timelineSubmission.batch_id ? batches[timelineSubmission.batch_id] : null;
              const current = getLabTimelineStatus(timelineSubmission, batch);
              const currentIndex = getLabTimelineOrder().indexOf(current.code);
              const stamps = {
                order_received: timelineSubmission.submitted_at,
                scheduled: timelineSubmission.blood_collection_time,
                drawn: ['completed', 'waiting_to_be_received', 'waiting_on_lab_results'].includes((timelineSubmission.status || '').toLowerCase()) ? timelineSubmission.blood_collection_time : null,
                batched: batch?.created_at,
                in_transit: batch?.shipped_at,
                waiting_results: timelineSubmission.received_at_lab,
                results_posted: timelineSubmission.results_posted_at,
              };
              return (
                <VStack align="stretch" spacing={0}>
                  {getLabTimelineOrder()
                    // hide branch/exception states unless they are the current state of this row
                    .filter(code => (code !== 'awaiting_info' && code !== 'rescheduling') || code === current.code)
                    .map((code, i, arr) => {
                      const cfg = getLabTimelineConfig(code);
                      const idx = getLabTimelineOrder().indexOf(code);
                      const isPast = idx < currentIndex;
                      const isCurrent = idx === currentIndex;
                      const isFuture = idx > currentIndex;
                      const stamp = stamps[code];
                      return (
                        <HStack key={code} align="start" spacing={4} pb={4} position="relative">
                          {/* Vertical connector line */}
                          {i < arr.length - 1 && (
                            <Box position="absolute" left="14px" top="28px" bottom={0} w="2px" bg={isPast || isCurrent ? `${cfg.color}.300` : 'gray.200'} />
                          )}
                          <Flex
                            w="30px"
                            h="30px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg={isCurrent ? `${cfg.color}.500` : (isPast ? `${cfg.color}.100` : 'gray.100')}
                            color={isCurrent ? 'white' : (isPast ? `${cfg.color}.600` : 'gray.400')}
                            border="2px solid"
                            borderColor={isCurrent ? `${cfg.color}.500` : (isPast ? `${cfg.color}.200` : 'gray.200')}
                            flexShrink={0}
                          >
                            {isPast ? <Icon as={FaCheckCircle} boxSize={3.5} /> : (isCurrent ? <Box w={2} h={2} bg="white" borderRadius="full" /> : <Box w={1.5} h={1.5} bg="gray.400" borderRadius="full" />)}
                          </Flex>
                          <Box flex={1} pt={0.5}>
                            <Text fontWeight={isCurrent ? '700' : '600'} fontSize="sm" color={isFuture ? 'gray.400' : 'gray.800'}>
                              {cfg.label}
                              {isCurrent && (
                                <Badge ml={2} colorScheme={cfg.color} fontSize="2xs" variant="subtle">Current</Badge>
                              )}
                            </Text>
                            <Text fontSize="xs" color={isFuture ? 'gray.400' : 'gray.500'}>{cfg.description}</Text>
                            {stamp && (
                              <Text fontSize="2xs" color="gray.400" mt={0.5}>{formatDate(stamp)}</Text>
                            )}
                            {/* On the active step, show the latest disposition
                                note + when it was logged so the lab sees the
                                most recent context inline. */}
                            {isCurrent && timelineSubmission.outcome_notes && (
                              <Box
                                mt={2}
                                p={2}
                                bg={`${cfg.color}.50`}
                                borderLeft="3px solid"
                                borderColor={`${cfg.color}.300`}
                                borderRadius="sm"
                              >
                                <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                                  "{timelineSubmission.outcome_notes}"
                                </Text>
                                {timelineSubmission.outcome_logged_at && (
                                  <Text fontSize="2xs" color="gray.500" mt={1}>
                                    {new Date(timelineSubmission.outcome_logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                                  </Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        </HStack>
                      );
                    })}
                </VStack>
              );
            })()}

            {/* All notes — the full disposition history for this sample, newest
                first, tagged with who logged each one. */}
            {timelineSubmission && (
              <Box mt={2}>
                <Divider mb={4} />
                <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={3}>
                  Notes &amp; Updates
                </Text>
                {timelineEventsLoading ? (
                  <Spinner size="sm" color="purple.400" />
                ) : (() => {
                  const withNotes = timelineEvents.filter((ev) => ev.outcome_notes);
                  if (withNotes.length === 0) {
                    // Fall back to the single denormalized note if there's no event history yet.
                    if (timelineSubmission.outcome_notes) {
                      return (
                        <Box bg="gray.50" borderLeft="3px solid" borderColor="gray.300" px={3} py={2} borderRadius="sm">
                          <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">"{timelineSubmission.outcome_notes}"</Text>
                        </Box>
                      );
                    }
                    return <Text fontSize="sm" color="gray.400">No notes logged yet.</Text>;
                  }
                  return (
                    <VStack align="stretch" spacing={3} divider={<Divider />}>
                      {withNotes.map((ev) => {
                        const cfg = ev.outcome_code ? getOutcomeConfig(ev.outcome_code) : null;
                        const who = actorBadge(ev.logged_by_kind);
                        const when = ev.logged_at
                          ? new Date(ev.logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                          : null;
                        return (
                          <Box key={ev.id}>
                            <HStack spacing={2} mb={1} flexWrap="wrap">
                              {cfg && (
                                <Badge colorScheme={cfg.color} fontSize="xs" borderRadius="full" px={2} variant="solid">
                                  {cfg.label}
                                </Badge>
                              )}
                              <Badge fontSize="2xs" colorScheme={who.color} variant="subtle">by {who.label}</Badge>
                              {when && <Text fontSize="2xs" color="gray.500">{when}</Text>}
                            </HStack>
                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">"{ev.outcome_notes}"</Text>
                          </Box>
                        );
                      })}
                    </VStack>
                  );
                })()}
              </Box>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100">
            <Button size="sm" variant="ghost" onClick={() => setTimelineSubmission(null)}>Close</Button>
            <Button size="sm" colorScheme="blue" ml={2} onClick={() => {
              const s = timelineSubmission;
              setTimelineSubmission(null);
              if (s) handleViewSubmission(s);
            }}>
              Open Details
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Submission Detail Modal ──────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
            <HStack spacing={3}>
              <Flex w={10} h={10} borderRadius="lg" bg="blue.50" align="center" justify="center">
                <Icon as={FaVial} color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="bold">{selectedSubmission?.patient_name}</Text>
                <HStack spacing={2}>
                  {selectedSubmission && (
                    <Badge colorScheme={getStatusColor(selectedSubmission.status)} fontSize="xs" borderRadius="full" px={2}>
                      {getStatusLabel(selectedSubmission.status)}
                    </Badge>
                  )}
                  <Text fontSize="xs" color="gray.400">
                    Submitted {selectedSubmission && formatDate(selectedSubmission.submitted_at)}
                  </Text>
                </HStack>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            {selectedSubmission && (
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                <GridItem>
                  <VStack align="stretch" spacing={5}>
                    {/* Patient Info */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        Patient Information
                      </Text>
                      <VStack align="start" spacing={1.5} fontSize="sm">
                        <HStack><Text color="gray.500" w="60px">Name</Text><Text fontWeight="medium">{selectedSubmission.patient_name}</Text></HStack>
                        <HStack><Text color="gray.500" w="60px">Email</Text><Text>{selectedSubmission.patient_email}</Text></HStack>
                        <HStack><Text color="gray.500" w="60px">DOB</Text><Text>{selectedSubmission.patient_dob}</Text></HStack>
                        <HStack><Text color="gray.500" w="60px">Gender</Text><Text>{selectedSubmission.patient_gender || 'N/A'}</Text></HStack>
                        <HStack align="start"><Text color="gray.500" w="60px">Address</Text><Text>{selectedSubmission.patient_address}</Text></HStack>
                      </VStack>
                    </Box>

                    {/* Doctor Info */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        Doctor Information
                      </Text>
                      <VStack align="start" spacing={1.5} fontSize="sm">
                        <HStack><Text color="gray.500" w="60px">Name</Text><Text fontWeight="medium">{selectedSubmission.doctor_name}</Text></HStack>
                        <HStack><Text color="gray.500" w="60px">Email</Text><Text>{selectedSubmission.doctor_email}</Text></HStack>
                        <HStack><Text color="gray.500" w="60px">Phone</Text><Text>{selectedSubmission.doctor_phone}</Text></HStack>
                        <HStack align="start"><Text color="gray.500" w="60px">Address</Text><Text>{selectedSubmission.doctor_address}</Text></HStack>
                      </VStack>
                    </Box>

                    {/* Test Info */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        Collection Details
                      </Text>
                      <VStack align="start" spacing={1.5} fontSize="sm">
                        <HStack><Text color="gray.500" minW="60px">Time</Text><Text>{selectedSubmission.blood_collection_time}</Text></HStack>
                        <HStack align="start"><Text color="gray.500" minW="60px">Notes</Text><Text>{selectedSubmission.special_instructions || 'None'}</Text></HStack>
                      </VStack>

                      {/* Status updates — every disposition logged on this draw
                          by a phleb, admin, or the lab, newest first. Each is
                          tagged with who logged it. */}
                      {(() => {
                        const hasEvents = statusEvents && statusEvents.length > 0;
                        const fallbackCfg = !hasEvents && selectedSubmission.outcome_code ? getOutcomeConfig(selectedSubmission.outcome_code) : null;
                        if (!statusEventsLoading && !hasEvents && !fallbackCfg && !selectedSubmission.outcome_notes) return null;
                        return (
                          <Box mt={4} p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={3}>
                              Status Updates
                            </Text>
                            {statusEventsLoading ? (
                              <Spinner size="sm" color="blue.400" />
                            ) : hasEvents ? (
                              <VStack align="stretch" spacing={3} divider={<Divider />}>
                                {statusEvents.map((ev) => {
                                  const cfg = ev.outcome_code ? getOutcomeConfig(ev.outcome_code) : null;
                                  const who = actorBadge(ev.logged_by_kind);
                                  const when = ev.logged_at
                                    ? new Date(ev.logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                                    : null;
                                  return (
                                    <Box key={ev.id}>
                                      <HStack spacing={2} mb={ev.outcome_notes ? 1 : 0} flexWrap="wrap">
                                        {cfg && (
                                          <Badge colorScheme={cfg.color} fontSize="xs" borderRadius="full" px={2} variant="solid">
                                            {cfg.label}
                                          </Badge>
                                        )}
                                        <Badge fontSize="2xs" colorScheme={who.color} variant="subtle">by {who.label}</Badge>
                                        {when && <Text fontSize="2xs" color="gray.500">{when}</Text>}
                                      </HStack>
                                      {ev.outcome_notes && (
                                        <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">"{ev.outcome_notes}"</Text>
                                      )}
                                    </Box>
                                  );
                                })}
                              </VStack>
                            ) : (
                              // Legacy fallback for draws with no event rows yet.
                              <Box>
                                <HStack spacing={2} mb={selectedSubmission.outcome_notes ? 2 : 0}>
                                  {fallbackCfg && (
                                    <Badge colorScheme={fallbackCfg.color} fontSize="xs" borderRadius="full" px={2} variant="solid">
                                      {fallbackCfg.label}
                                    </Badge>
                                  )}
                                  {selectedSubmission.outcome_logged_at && (
                                    <Text fontSize="xs" color="gray.500">
                                      {new Date(selectedSubmission.outcome_logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                                    </Text>
                                  )}
                                </HStack>
                                {selectedSubmission.outcome_notes && (
                                  <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">"{selectedSubmission.outcome_notes}"</Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                      {/* Tests on script */}
                      {(() => {
                        let tests = [];
                        const raw = selectedSubmission.tests_on_the_script;
                        if (Array.isArray(raw)) tests = raw;
                        else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
                          try { tests = JSON.parse(raw); } catch {}
                        }
                        if (tests.length === 0) return null;
                        return (
                          <Box mt={3} w="full">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                              Tests on Script
                            </Text>
                            <Box overflowX="auto">
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th fontSize="2xs">Test</Th>
                                    <Th fontSize="2xs">Description</Th>
                                    <Th fontSize="2xs">Price</Th>
                                    <Th fontSize="2xs">Dx Code</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {tests.map((t, idx) => (
                                    <Tr key={idx}>
                                      <Td fontSize="xs">{t?.name || t?.id || '\u2014'}</Td>
                                      <Td fontSize="xs">{t?.description || '\u2014'}</Td>
                                      <Td fontSize="xs">{t?.price != null ? `$${t.price}` : 'N/A'}</Td>
                                      <Td fontSize="xs">
                                        <Badge colorScheme={t?.diagnosis_code_present ? 'green' : 'yellow'} fontSize="2xs">
                                          {t?.diagnosis_code_present ? 'Yes' : 'Unconfirmed'}
                                        </Badge>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  </VStack>
                </GridItem>

                <GridItem>
                  <VStack align="stretch" spacing={5}>
                    {/* Documents */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        Documents
                      </Text>
                      {(() => {
                        const primary = normalizeToUrls(selectedSubmission.script_image);
                        const fallback = normalizeToUrls(selectedSubmission.script_url);
                        const docs = [...primary, ...fallback];
                        return docs.length > 0
                          ? docs.map((url, idx) => <FilePreview key={`script_${idx}`} url={url} label={`Doctor's Script ${idx + 1}`} />)
                          : <Text fontSize="xs" color="gray.400">No script document uploaded</Text>;
                      })()}

                      {(() => {
                        const docs = normalizeToUrls(selectedSubmission.insurance_card_image);
                        return docs.length > 0
                          ? docs.map((url, idx) => <FilePreview key={`ins_${idx}`} url={url} label={`Insurance Card ${idx + 1}`} />)
                          : <Text fontSize="xs" color="gray.400" mt={2}>No insurance card on file</Text>;
                      })()}

                      {(() => {
                        const docs = normalizeToUrls(selectedSubmission.patient_id_image);
                        return docs.length > 0
                          ? docs.map((url, idx) => <FilePreview key={`pid_${idx}`} url={url} label={`Patient ID ${idx + 1}`} />)
                          : <Text fontSize="xs" color="gray.400" mt={2}>No patient ID on file</Text>;
                      })()}

                      {selectedSubmission.fedex_label_url && (
                        <FilePreview url={selectedSubmission.fedex_label_url} label={`FedEx Label${selectedSubmission.fedex_tracking_number ? ' \u2013 ' + selectedSubmission.fedex_tracking_number : ''}`} />
                      )}
                    </Box>

                    {/* Lab Results */}
                    {selectedSubmission.lab_results_url && (
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                          Lab Results
                        </Text>
                        <Button as="a" href={selectedSubmission.lab_results_url} target="_blank" size="sm" colorScheme="green" leftIcon={<FaEye />}>
                          View Results
                        </Button>
                      </Box>
                    )}

                    {/* Upload Results Section */}
                    {(selectedSubmission.status === 'waiting_on_lab_results' || (selectedSubmission.status === 'completed' && !selectedSubmission.lab_results_url)) && !selectedSubmission.lab_results_url && (
                      <Box p={4} bg="orange.50" borderRadius="lg" border="1px solid" borderColor="orange.200">
                        <HStack mb={2}>
                          <Icon as={FaUpload} color="orange.500" boxSize={4} />
                          <Text fontWeight="semibold" color="orange.700" fontSize="sm">Upload Lab Results</Text>
                        </HStack>
                        <Text fontSize="xs" color="orange.600" mb={3}>
                          Upload results to complete this submission.
                        </Text>
                        <FormControl>
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            size="sm"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleUploadResults(file, selectedSubmission);
                                e.target.value = '';
                              }
                            }}
                          />
                          <Text fontSize="2xs" color="gray.400" mt={1}>
                            PDF, DOC, DOCX, JPG, JPEG, PNG
                          </Text>
                        </FormControl>
                      </Box>
                    )}
                  </VStack>
                </GridItem>
              </Grid>
            )}
          </ModalBody>

          {/* Modal actions */}
          {selectedSubmission && (selectedSubmission.status === 'pending' || selectedSubmission.status === 'in_progress' || selectedSubmission.status === 'waiting_to_be_received' || (selectedSubmission.status === 'completed' && !selectedSubmission.lab_results_url)) && (
            <ModalFooter borderTop="1px solid" borderColor="gray.100" pt={3}>
              <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
              <Button
                colorScheme="teal"
                variant="outline"
                mr={3}
                leftIcon={<FaCalendarPlus />}
                onClick={() => handleOpenSchedule(selectedSubmission)}
              >
                Schedule
              </Button>
              <Button
                colorScheme="purple"
                leftIcon={<FaCheckCircle />}
                onClick={() => handleMarkReceived(selectedSubmission)}
              >
                Mark as Received
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      {/* ── Delete Confirmation Modal ────────────────────────────────────────── */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={onDeleteConfirmClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="md">Delete Secret Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600">
              Are you sure you want to delete this key? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" size="sm" mr={2} onClick={onDeleteConfirmClose}>Cancel</Button>
            <Button colorScheme="red" size="sm" onClick={confirmDeleteSecretKey}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Schedule Appointment Modal ───────────────────────────────────────── */}
      <Modal isOpen={isScheduleOpen} onClose={onScheduleClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaCalendarPlus} color="teal.500" />
              <Text fontSize="md">Schedule appointment</Text>
            </HStack>
            {scheduleSubmission && (
              <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
                {scheduleSubmission.patient_name}
                {scheduleSubmission.draw_code ? ` · #${scheduleSubmission.draw_code}` : ''}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Date & time</FormLabel>
                <Input
                  type="datetime-local"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Note (optional)</FormLabel>
                <Textarea
                  value={scheduleNote}
                  onChange={(e) => setScheduleNote(e.target.value)}
                  placeholder="e.g. patient confirmed for Wednesday 3pm"
                  rows={3}
                />
              </FormControl>
              <Text fontSize="xs" color="gray.500">
                This sets the visit time and logs a “Scheduled” update tagged to your lab. The
                phlebotomist will see the new time on their side.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onScheduleClose} isDisabled={scheduleSaving}>Cancel</Button>
            <Button colorScheme="teal" leftIcon={<FaCalendarPlus />} onClick={handleSaveSchedule} isLoading={scheduleSaving}>
              Save appointment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LabPortalDashboard;
